import axios from "axios";
import { getSessionToken } from "@descope/react-sdk";
import { AppMode } from "@/lib/config/types";
import { routeToTenantCell } from "@/lib/cells/route-to-tenant-cell";
import { PRIMARY_API_BASE_URL } from "@/lib/config/api-base";

interface StoredConfig {
  mode?: AppMode;
  localBaseUrl?: string;
  localTenantId?: string;
  [key: string]: unknown;
}

const getStoredConfig = (): StoredConfig | null => {
  try {
    const stored = localStorage.getItem("harmony-management-config");
    if (stored) {
      return JSON.parse(stored) as StoredConfig;
    }
  } catch (error) {
    console.error("Failed to load config from localStorage:", error);
  }
  return null;
};

export const backofficeClient = axios.create();

export const BACKOFFICE_SERVICE_PATH = "/backoffice-api/api/v1";

export const configureBackofficeClient = (): void => {
  try {
    const config = getStoredConfig();
    const mode = config?.mode ?? AppMode.REMOTE;

    let baseUrl: string;
    if (mode === AppMode.LOCAL) {
      const localBaseUrl = config?.localBaseUrl ?? "http://localhost:8000";
      baseUrl = `${localBaseUrl}/api/v1`;
    } else if (import.meta.env.DEV) {
      baseUrl = BACKOFFICE_SERVICE_PATH;
    } else {
      baseUrl =
        (import.meta.env.VITE_BACKOFFICE_API_URL as string | undefined) ??
        `${PRIMARY_API_BASE_URL}${BACKOFFICE_SERVICE_PATH}`;
    }

    backofficeClient.defaults.baseURL = baseUrl;
    backofficeClient.interceptors.request.use(async (v) => {
      if (
        mode === AppMode.REMOTE &&
        (await routeToTenantCell(v, BACKOFFICE_SERVICE_PATH))
      ) {
        return v;
      }

      try {
        if (mode === AppMode.REMOTE) {
          const token = getSessionToken();
          if (token) {
            v.headers.set("Authorization", `Bearer ${token}`);
          }
        } else {
          const localTenantId = config?.localTenantId;
          if (localTenantId) {
            v.headers.set("x-tenant-id", localTenantId);
          }
        }

        if (import.meta.env.DEV && import.meta.env.VITE_FORCE_TENANT_ID && !v.headers.get("x-tenant-id")) {
          v.headers.set("x-tenant-id", import.meta.env.VITE_FORCE_TENANT_ID);
        }
      } catch (error: unknown) {
        console.error("Failed to configure Backoffice API request headers:", error);
      }
      return v;
    });
  } catch (error) {
    console.error("Failed to configure Backoffice API client:", error);
  }
};

configureBackofficeClient();
